import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiSearch,
  FiFilter,
  FiPackage,
  FiCheckCircle,
  FiAlertTriangle,
  FiTruck,
  FiBarChart2,
} from "react-icons/fi";
import { dashboardApi } from "../services/dashboardApi";
import ContainerJourneyTimeline from "../components/ContainerJourneyTimeline";
import ContainerJourneyMap from "../components/ContainerJourneyMap";

const emptyDashboard = {
  summary: {
    totalPayable: 0,
    finalBalance: 0,
    outstandingInvoices: 0,
    paidInvoices: 0,
    paidAmount: 0,
    totalContainers: 0,
    inTransit: 0,
    delivered: 0,
    customsCleared: 0,
    arrivedAtPort: 0,
    departedFromOriginPort: 0,
    activeVendors: 0,
    totalValue: 0,
    totalCostPrices: 0,
    avgCost: 0,
    avgShipping: 0,
    totalReceived: 0,
    outstandingAmount: 0,
    completedPayments: 0,
    pendingPayments: 0,
    failedPayments: 0,
    refundedPayments: 0,
  },
  trend: [],
  maxTrend: 1,
  containerShipments: [],
  containerStatusBreakdown: [],
  routeHighlights: [],
  recentInvoices: [],
  recentCostPrices: [],
  meta: {
    totalInvoices: 0,
    totalPayments: 0,
    totalVendors: 0,
    totalContainers: 0,
    totalCostPrices: 0,
    updatedAt: null,
  },
};

const formatMoney = (value) =>
  `£${Number(value || 0).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const statusClass = (status) => {
  const s = String(status || "").toLowerCase();

  if (s === "paid" || s === "completed" || s === "delivered" || s === "active") {
    return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-900";
  }

  if (s === "pending" || s === "review") {
    return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900";
  }

  if (s === "overdue" || s === "failed" || s === "customs hold") {
    return "bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-900";
  }

  if (s === "in transit" || s === "departed from origin port") {
    return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900";
  }

  if (s === "arrived at port") {
    return "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950 dark:text-violet-300 dark:border-violet-900";
  }

  if (s === "customs cleared") {
    return "bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-950 dark:text-cyan-300 dark:border-cyan-900";
  }

  return "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
};

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-3xl border border-slate-200 bg-white shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900 ${className}`}
    >
      {children}
    </div>
  );
}

function SectionHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5 dark:border-slate-800">
      <div>
        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
          {title}
        </h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {subtitle}
        </p>
      </div>
      {action}
    </div>
  );
}

function StatusPill({ children, className = "" }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold capitalize ${className}`}
    >
      {children}
    </span>
  );
}

function StatCard({ title, value, sub, icon: Icon, iconBg }) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {title}
          </p>
          <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-slate-100">
            {value}
          </div>
          {sub ? (
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              {sub}
            </p>
          ) : null}
        </div>

        <div className={`grid h-12 w-12 place-items-center rounded-2xl ${iconBg}`}>
          <Icon className="text-xl text-white" />
        </div>
      </div>
    </Card>
  );
}

function RouteHighlight({ item }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/60">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {item.bookingBLNo}
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {item.containerNumber}
          </p>
        </div>
        <StatusPill className={statusClass(item.status)}>{item.status}</StatusPill>
      </div>

      <div className="mt-3 text-sm text-slate-600 dark:text-slate-300">
        {item.routeLabel}
      </div>

      <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
        <div
          className="h-full rounded-full bg-blue-500"
          style={{ width: `${item.routeProgress || 0}%` }}
        />
      </div>

      <div className="mt-2 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
        <span>{item.routeState}</span>
        <span>{item.routeProgress || 0}%</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(emptyDashboard);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await dashboardApi.getOverview();
        const data = res.data || emptyDashboard;

        setDashboard({
          summary: { ...emptyDashboard.summary, ...(data.summary || {}) },
          trend: Array.isArray(data.trend) ? data.trend : [],
          maxTrend: Number(data.maxTrend || 1),
          containerShipments: Array.isArray(data.containerShipments)
            ? data.containerShipments
            : [],
          containerStatusBreakdown: Array.isArray(data.containerStatusBreakdown)
            ? data.containerStatusBreakdown
            : [],
          routeHighlights: Array.isArray(data.routeHighlights)
            ? data.routeHighlights
            : [],
          recentInvoices: Array.isArray(data.recentInvoices)
            ? data.recentInvoices
            : [],
          recentCostPrices: Array.isArray(data.recentCostPrices)
            ? data.recentCostPrices
            : [],
          meta: { ...emptyDashboard.meta, ...(data.meta || {}) },
        });
      } catch (err) {
        console.error("DASHBOARD LOAD ERROR:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const filterByDate = (item) => {
    const d = item?.date ? new Date(item.date) : null;
    if (!d || Number.isNaN(d.getTime())) return true;

    if (fromDate) {
      const from = new Date(fromDate);
      if (d < from) return false;
    }

    if (toDate) {
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      if (d > to) return false;
    }

    return true;
  };

  const filteredShipments = useMemo(
    () => dashboard.containerShipments.filter(filterByDate),
    [dashboard.containerShipments, fromDate, toDate]
  );

  const filteredHighlights = useMemo(
    () => dashboard.routeHighlights.filter(filterByDate),
    [dashboard.routeHighlights, fromDate, toDate]
  );

  const featuredShipment = filteredShipments[0] || null;

  const topStats = useMemo(
    () => [
      {
        label: "Total Containers",
        value: dashboard.summary.totalContainers,
        sub: "Live shipment count",
        icon: FiPackage,
        iconBg: "bg-blue-50 dark:bg-blue-950",
      },
      {
        label: "In Transit",
        value: dashboard.summary.inTransit,
        sub: "Currently moving",
        icon: FiTruck,
        iconBg: "bg-amber-50 dark:bg-amber-950",
      },
      {
        label: "Arrived at Port",
        value: dashboard.summary.arrivedAtPort,
        sub: "Reached destination port",
        icon: FiCheckCircle,
        iconBg: "bg-violet-50 dark:bg-violet-950",
      },
      {
        label: "Delivered",
        value: dashboard.summary.delivered,
        sub: "Completed shipments",
        icon: FiCheckCircle,
        iconBg: "bg-emerald-50 dark:bg-emerald-950",
      },
    ],
    [dashboard]
  );

  const financialStats = useMemo(
    () => [
      {
        title: "Total Payable",
        value: formatMoney(dashboard.summary.totalPayable),
        sub: `${dashboard.summary.outstandingInvoices} outstanding invoices`,
        icon: FiAlertTriangle,
        iconBg: "bg-amber-500",
      },
      {
        title: "Final Balance",
        value: formatMoney(dashboard.summary.finalBalance),
        sub: "All invoice balances combined",
        icon: FiCheckCircle,
        iconBg: "bg-emerald-500",
      },
      {
        title: "Outstanding Invoices",
        value: dashboard.summary.outstandingInvoices,
        sub: "Awaiting payment",
        icon: FiPackage,
        iconBg: "bg-orange-400",
      },
      {
        title: "Paid Invoices",
        value: dashboard.summary.paidInvoices,
        sub: formatMoney(dashboard.summary.paidAmount),
        icon: FiCheckCircle,
        iconBg: "bg-emerald-500",
      },
    ],
    [dashboard]
  );

  return (
    <div className="min-h-screen space-y-6 bg-slate-50/60 p-6 text-slate-900 transition-colors duration-300 dark:bg-slate-950 dark:text-slate-100">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
            Dashboard Overview
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Live data from invoices, payments, containers, vendors, and cost prices.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <FiSearch className="text-sm text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search invoices, containers..."
              className="w-52 bg-transparent text-sm outline-none placeholder:text-slate-400 dark:text-slate-400"
            />
          </div>

          <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-[140px] bg-transparent text-sm outline-none dark:[color-scheme:dark]"
              title="From date"
            />
            <span className="text-xs text-slate-400">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-[140px] bg-transparent text-sm outline-none dark:[color-scheme:dark]"
              title="To date"
            />
            {(fromDate || toDate) && (
              <button
                type="button"
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                }}
                className="rounded-full px-2 py-1 text-xs text-slate-500 transition hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
              >
                Clear
              </button>
            )}
          </div>

          <Link
            to="/reports"
            className="flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
          >
            <FiBarChart2 className="text-sm" />
            View Reports
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        {topStats.map((item) => (
          <StatCard
            key={item.label}
            title={item.label}
            value={item.value}
            sub={item.sub}
            icon={item.icon}
            iconBg={item.iconBg}
          />
        ))}
      </div>

      <Card className="overflow-hidden">
        <SectionHeader
          title="Live Container Tracking"
          subtitle="Map-style sea route tracking for containers moving from China to the UK"
          action={
            <div className="flex items-center gap-2">
              <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800">
                <FiFilter className="text-sm" />
                Filter
              </button>
              <Link
                to="/containers"
                className="rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700"
              >
                + Add Container
              </Link>
            </div>
          }
        />

        <div className="grid gap-6 p-6 lg:grid-cols-[0.92fr_1.08fr]">
          <div>
            {featuredShipment ? (
              <ContainerJourneyTimeline shipment={featuredShipment} />
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
                No shipment data available for the selected date range.
              </div>
            )}
          </div>

          <div>
            {featuredShipment ? (
              <ContainerJourneyMap shipment={featuredShipment} />
            ) : (
              <div className="grid h-[560px] place-items-center rounded-3xl border border-dashed border-slate-300 bg-white text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
                No map data available for the selected date range.
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        {financialStats.map((item) => (
          <StatCard
            key={item.title}
            title={item.title}
            value={item.value}
            sub={item.sub}
            icon={item.icon}
            iconBg={item.iconBg}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card>
          <SectionHeader
            title="Container Status Summary"
            subtitle="Latest route highlights"
            action={
              <Link
                to="/containers"
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                View All →
              </Link>
            }
          />

          <div className="space-y-3 px-6 py-5">
            {filteredHighlights.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 p-4 text-sm text-slate-500 dark:border-slate-800 dark:text-slate-400">
                No container highlights found for the selected date range.
              </div>
            ) : (
              filteredHighlights.map((item) => (
                <RouteHighlight key={item.id} item={item} />
              ))
            )}
          </div>
        </Card>

        <Card>
          <SectionHeader
            title="Recent Invoices"
            subtitle="Latest transactions"
            action={
              <Link
                to="/invoices"
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                View All →
              </Link>
            }
          />

          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {loading ? (
              <div className="px-6 py-8 text-sm text-slate-500 dark:text-slate-400">
                Loading invoices...
              </div>
            ) : dashboard.recentInvoices.length === 0 ? (
              <div className="px-6 py-8 text-sm text-slate-500 dark:text-slate-400">
                No invoices found.
              </div>
            ) : (
              dashboard.recentInvoices.map((item) => (
                <div
                  key={item.id}
                  className="px-6 py-4 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/60"
                >
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-100">
                        {item.ref}
                      </span>
                      <StatusPill className={statusClass(item.status)}>
                        {item.status}
                      </StatusPill>
                    </div>
                    <span className="text-sm font-bold text-slate-800 dark:text-slate-100">
                      {formatMoney(item.total)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                    <span>{formatDate(item.date)}</span>
                    <span>{item.supplier}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}